import { generateUUID } from 'app/shared/utils/uuid.util';
import { Injectable } from '@angular/core';

/**
 * Provides access to auxiliary AppInsights telemetry client.
 * This is a separate AI client so part of the telemetry can be collected into a separate AppInsights account.
 */
@Injectable({
    providedIn: 'root'
})
export class AuxiliaryLoggerService {
    private readonly _aiAuxFrame: HTMLIFrameElement | undefined;
    private readonly _pendingRequests = new Map<string, () => void>();
    private readonly _aiAuxFrameInitPromise: Promise<boolean>;

    public constructor() {
        if (__IS_A365__) {
            this._aiAuxFrame = document.createElement('iframe');
            this._aiAuxFrame.style.position = 'absolute';
            this._aiAuxFrame.style.left = '0';
            this._aiAuxFrame.style.top = '0';
            this._aiAuxFrame.style.width = '1px';
            this._aiAuxFrame.style.height = '1px';
            this._aiAuxFrame.style.display = 'none';
            this._aiAuxFrame.src = '/ai_aux.html';
            document.body.appendChild(this._aiAuxFrame);
            // ensure that App Insight iframe is loaded
            this._aiAuxFrameInitPromise = new Promise<boolean>((resolve) => {
                window.addEventListener('message', (e) => {
                    if (e.source !== this._aiAuxFrame.contentWindow) {
                        return;
                    }
                    if (e.data && typeof e.data === 'object' && e.data.action === 'ai_aux.initialized') {
                        resolve(true);
                    }
                });
            });
            window.addEventListener('message', (e) => {
                if (e.source !== this._aiAuxFrame.contentWindow) {
                    return;
                }
                if (e.data && typeof e.data === 'object' && e.data.action === 'ai_aux.response') {
                    const ack = this._pendingRequests.get(e.data.id);
                    if (ack) {
                        ack();
                    }
                }
            });
        }
    }

    public trackEvent(name: string, properties?: Record<string, unknown>): Promise<void> {
        return this._sendRequest('trackEvent', [{ name, properties: this._applyContext(properties) }]);
    }

    public flush(): Promise<void> {
        return this._sendRequest('flush');
    }

    private _applyContext(properties?: Record<string, unknown>): Record<string, unknown> {
        const { subscriptionId = undefined, resourceGroupName = undefined, workspaceName = undefined, environment = undefined } = appInsights.loggingContext || {};
        return {
            ...properties,
            environment,
            subscriptionId,
            resourceGroupName,
            workspaceName
        };
    }

    private async _sendRequest(action: string, args?: unknown[]): Promise<void> {
        if (!this._aiAuxFrame) {
            return Promise.resolve(undefined);
        }
        // ensure that App Insight iframe is loaded to prevent message loss
        await this._aiAuxFrameInitPromise;
        const id = generateUUID();
        this._aiAuxFrame.contentWindow.postMessage({
            id,
            action,
            args
        }, self.origin);
        return new Promise<void>((res) => {
            this._pendingRequests.set(id, res);
        });
    }
}
